import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PiCar, PiCarProfile, PiUsers } from 'react-icons/pi'

import { useKycStore } from '@store/useKycStore'
import { useUserStore } from '@/store/useUserStore'

import Button from '@/components/common/Button/Button'
import ProgressBar from '@components/common/ProgressBar/ProgressBar'
import MotionText from '@/components/common/Motion/MotionText'
import Radio from '@components/common/Radio/Radio'
import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import { usePageSetup } from '@/hooks/usePageSetup'

import kycAPI from '@/services/api/kyc'

export const KycVehicleTypePage = () => {
  const { t } = useTranslation()
  usePageSetup(t('kyc.pageTitle.vehicleType'), { scroll: true })

  const user = useUserStore((state) => state.user)
  const setUser = useUserStore((state) => state.setUser)

  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  const [isLoading, setIsLoading] = useState(false)
  const [selectedVehicleType, setSelectedVehicleType] = useState(user?.kyc?.vehicle_type || '')

  useEffect(() => setProgress(3), [])

  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)

    try {
      setIsLoading(true)

      const vehicleType = formData.get('vehicleType') as string

      const response = await kycAPI.create({
        kyc: {
          vehicle_type: vehicleType
        }
      })

      user && setUser({ ...user, kyc: response.kyc })

      if (vehicleType === 'rented') {
        navigate("/kyc/vat")
      } else {
        navigate("/kyc/vehicle")
      }
    } catch (error: any) {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="h-full bg-white p-5 pb-8 flex flex-col">
      <HeaderBack className='items-center gap-4'>
        <ProgressBar
          currentStep={progress}
          totalSteps={8}
        />
      </HeaderBack>
      <div className="flex-1 flex flex-col mt-4">
        <div className="space-y-4">
          <MotionText
            as="h2"
            className="text-zinc-900 text-3xl font-bold leading-tight"
            delay={0.2}
          >
            {t('kyc.vehicleType.title')}
          </MotionText>
          <MotionText
            as="p"
            className="text-zinc-700 text-base"
            delay={0.4}
          >
            {t('kyc.vehicleType.description')}
          </MotionText>
        </div>
        <motion.div
          className='flex flex-col mt-8 mb-24 gap-3'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            delay: 0.2,
            duration: 0.2,
            ease: "easeOut"
          }}
        >
          <div className="space-y-3">
            <Radio
              required
              name="vehicleType"
              value="rented"
              label={t('kyc.vehicleType.rented')}
              subtext={t('kyc.vehicleType.rentedSubtext')}
              className='pb-4 border-b border-zinc-200'
              icon={<PiCarProfile className="h-6 w-6 text-zinc-900" />}
              checked={selectedVehicleType === 'rented'}
              onChange={(e) => setSelectedVehicleType(e.target.value)}
            />
            <Radio
              required
              name="vehicleType"
              value="owned"
              label={t('kyc.vehicleType.owned')}
              className='pb-4 border-b border-zinc-200'
              icon={<PiCar className="h-6 w-6 text-zinc-900" />}
              checked={selectedVehicleType === 'owned'}
              onChange={(e) => setSelectedVehicleType(e.target.value)}
            />
            <Radio
              required
              name="vehicleType"
              value="other_owned"
              label={t('kyc.vehicleType.otherOwned')}
              subtext={t('kyc.vehicleType.otherOwnedSubtext')}
              icon={<PiUsers className="h-6 w-6 text-zinc-900" />}
              checked={selectedVehicleType === 'other_owned'}
              onChange={(e) => setSelectedVehicleType(e.target.value)}
            />
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 ml-[-1.25rem] pl-5 pr-5 pb-8 w-full bg-linear-to-t from-white via-white to-transparent">
        <Button isLoading={isLoading} type='submit'>
          <motion.span
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeOut"
            }}
          >
            {t('common.save')}
          </motion.span>
        </Button>
      </div>
    </form>
  )
}

export default KycVehicleTypePage